/** Pagina di consultazione dei portafogli già importati. */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { PORTFOLIO_TYPE_LABELS, formatCurrency, formatDate, formatNumber, labelFor } from '@/lib/format';
import { Alert, Badge, Button, Card, DomainBadges, EmptyState, Spinner, TableWrap } from '@/ui';
import { CreatePortfolioModal } from './CreatePortfolioModal';
import { usePortfolios } from './api';
import './portfolios.css';

export function PortfolioPage() {
  const portfoliosQuery = usePortfolios();
  const [showCreate, setShowCreate] = useState(false);

  if (portfoliosQuery.isPending) return <Spinner label="Caricamento dei portafogli…" />;

  if (portfoliosQuery.isError) {
    const error = portfoliosQuery.error;
    return (
      <Alert variant="error" title="Impossibile caricare i portafogli">
        {error instanceof ApiError ? error.message : 'Errore imprevisto.'}
      </Alert>
    );
  }

  const portfolios = portfoliosQuery.data.items;
  const activeCount = portfolios.filter((portfolio) => portfolio.isActive).length;

  return (
    <div className="portfolios">
      <header className="portfolios__header">
        <div>
          <h1 className="portfolios__title">Portafogli</h1>
          <p className="portfolios__subtitle">
            {formatNumber(activeCount)} attivi su {formatNumber(portfolios.length)} totali
          </p>
        </div>
        <div className="portfolios__header-actions">
          <Button variant="secondary" onClick={() => setShowCreate(true)}>
            Crea portafoglio
          </Button>
        </div>
      </header>

      {showCreate && <CreatePortfolioModal onClose={() => setShowCreate(false)} />}

      <Card title="I tuoi portafogli" subtitle="Valore dell'ultimo snapshot disponibile" flush>
        {portfolios.length === 0 ? (
          <EmptyState
            icon="💼"
            title="Nessun portafoglio"
            description="Importa un estratto per crearli automaticamente, oppure creane uno a mano."
            action={
              <div className="portfolios__empty-actions">
                <Link className="btn btn--primary" to="/imports">
                  Importa i tuoi dati
                </Link>
                <Button variant="secondary" onClick={() => setShowCreate(true)}>
                  Crea portafoglio
                </Button>
              </div>
            }
          />
        ) : (
          <TableWrap>
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th className="col--secondary">Piattaforma</th>
                  <th className="col--tertiary">Tipologia</th>
                  <th>Stato</th>
                  <th className="col--secondary">Ultimo snapshot</th>
                  <th className="numeric">Posizioni</th>
                  <th className="numeric col--tertiary">Movimenti</th>
                  <th className="numeric">Valore</th>
                </tr>
              </thead>
              <tbody>
                {portfolios.map((portfolio) => {
                  const snapshot = portfolio.latestSnapshot;
                  return (
                    <tr key={portfolio.id}>
                      <td>
                        {/* Il link sta sul nome e non sulla riga: resta
                            raggiungibile da tastiera e apribile in una scheda nuova. */}
                        <Link className="portfolios__link" to={`/portfolios/${portfolio.id}`}>
                          {portfolio.name}
                        </Link>
                      </td>
                      <td className="col--secondary">{portfolio.platform.name}</td>
                      <td className="col--tertiary">
                        <span className="portfolios__badges">
                          <Badge>{labelFor(PORTFOLIO_TYPE_LABELS, portfolio.portfolioType)}</Badge>
                          <DomainBadges domains={portfolio.domains} />
                        </span>
                      </td>
                      <td>
                        <Badge tone={portfolio.isActive ? 'positive' : 'neutral'}>
                          {portfolio.isActive ? 'Attivo' : 'Disattivato'}
                        </Badge>
                      </td>
                      <td className="cell--minor col--secondary">{formatDate(snapshot?.snapshotDate)}</td>
                      <td className="numeric">{formatNumber(snapshot?.holdingsCount ?? 0)}</td>
                      <td className="numeric col--tertiary">{formatNumber(portfolio.transactionCount)}</td>
                      <td className="numeric">
                        {snapshot ? formatCurrency(snapshot.totalValue, snapshot.currency) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}
